import { useEffect, useState } from "react";
import { ApiElementsResponse } from "../shared/interfaces";

/**
 * Merge two arrays of items, filter them, and remove duplicates by a specified key.
 *
 * @template T - The type of items in the arrays.
 * @param prev - The existing array of items.
 * @param incoming - The new array of items to merge in.
 * @param keyFn - Function that returns a unique key for each item (used for deduplication).
 * @param filterFn - Predicate function to filter items (only items where filterFn(item) is true will be kept).
 * @returns A new array containing merged, filtered, and deduplicated items.
 */
function mergeByKey<T>(
    prev: T[],
    incoming: T[],
    keyFn: (item: T) => string,
    filterFn: (item: T) => boolean,
): T[] {
    const map = new Map<string, T>();
    for (const item of [...prev, ...incoming]) {
        if (filterFn(item)) {
            map.set(keyFn(item), item);
        }
    }
    return Array.from(map.values());
}

/**
 * React hook to merge paginated query data by key, filter by file ID, and maintain state.
 *
 * @template T - The type of items within the query response arrays.
 * @param data - The ApiElementsResponse object containing two arrays: fragment_nodes and connections.
 * @param fileId - The ID of the file used to filter items.
 * @param prop - The key of data to merge (either "fragment_nodes" or "connections").
 * @param keyFn - Function that returns a unique key for each item of type T (used for deduplication).
 * @param fileIdFn - Function that returns the file ID from an item of type T (used for filtering by fileId).
 * @returns A stateful array of items that have been merged, filtered by fileId, and deduplicated.
 */
export function useMergedQueryData<T>(
    data: ApiElementsResponse | undefined,
    fileId: string,
    prop: keyof ApiElementsResponse,
    keyFn: (item: T) => string,
    fileIdFn: (item: T) => string,
): T[] {
    const [items, setItems] = useState<T[]>([]);

    useEffect(() => {
        if (!data) return;

        const incoming = data[prop] as T[];
        setItems((prev) => {
            const merged = mergeByKey(prev, incoming, keyFn, (item) => fileIdFn(item) === fileId);

            // simple ID-based comparison
            const same =
                merged.length === prev.length &&
                merged.every((v, i) => keyFn(v) === keyFn(prev[i]));

            return same ? prev : merged;
        });
        // note: only data and fileId really need to be in here
    }, [data, fileId, prop, fileIdFn, keyFn]);

    return items;
}
